import React from 'react';
import { styles, colors } from '../styles/styles';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Header component with user controls and brand name
 * Hidden on detail pages
 */
const Header = ({ user, onLogin, onLogout }) => {
    const location = useLocation();
    const navigate = useNavigate();
    
    // Navigate to analytics
    const handleAnalyticsClick = () => {
        navigate('/timeline');
    };
    
    // Hide header on detail pages
    const isDetailPage = location.pathname.includes('/thought/') || 
                         location.pathname.includes('/edit/') ||
                         location.pathname.includes('/edit-field/') ||
                         location.pathname.includes('/compose') ||
                         location.pathname.includes('/collection/');
    
    const handleIntentionsClick = () => {
        navigate('/intentions');
    };

    // Get current route for active button styling
    const isRoute = (path) => location.pathname === path;

    if (isDetailPage) {
        return null; // Don't render header on detail pages
    }
    
    const baseButtonStyle = {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: '6px',
        color: colors.textPrimary,
        display: 'flex',
        alignItems: 'center',
        borderRadius: '8px',
        WebkitTapHighlightColor: 'rgba(0,0,0,0)',
        transition: 'background-color 200ms ease',
    };
    
    return (
        <header style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 24px',
            paddingLeft: '24px', // Consistent padding
            marginBottom: '16px',
            backgroundColor: colors.background,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 25,
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        }}>
            {/* Left side buttons container */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
            }}>
                {/* Home button */}
                <button
                    onClick={() => navigate('/')}
                    className="ios-button"
                    style={{
                        ...baseButtonStyle,
                        backgroundColor: isRoute('/') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    }}
                    title="Home"
                >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9 22V12h6v10" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                {/* Intentions button */}
                <button
                    onClick={handleIntentionsClick}
                    className="ios-button"
                    style={{
                        ...baseButtonStyle,
                        backgroundColor: isRoute('/intentions') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    }}
                    title="Intentions"
                >
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="4" cy="4" r="2" stroke={colors.textPrimary} strokeWidth="1"/>
                        <path d="m14 5 3-3 3 3" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="m14 10 3-3 3 3" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M17 14V2" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M17 14H7l-5 8h20Z" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 14v8" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="m9 14 5 8" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                {/* Insights button */}
                <button
                    onClick={() => navigate('/insights')}
                    className="ios-button"
                    style={{
                        ...baseButtonStyle,
                        backgroundColor: isRoute('/insights') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    }}
                    title="Insights"
                >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="4" stroke={colors.textPrimary} strokeWidth="1"/>
                        <path d="M12 3V5" stroke={colors.textPrimary} strokeWidth="1.25" strokeLinecap="round"/>
                        <path d="M12 19V21" stroke={colors.textPrimary} strokeWidth="1.25" strokeLinecap="round"/>
                        <path d="M3 12H5" stroke={colors.textPrimary} strokeWidth="1.25" strokeLinecap="round"/>
                        <path d="M19 12H21" stroke={colors.textPrimary} strokeWidth="1.25" strokeLinecap="round"/>
                        <path d="M5.6 5.6L7 7" stroke={colors.textPrimary} strokeWidth="1.25" strokeLinecap="round"/>
                        <path d="M17 17L18.4 18.4" stroke={colors.textPrimary} strokeWidth="1.25" strokeLinecap="round"/>
                        <path d="M5.6 18.4L7 17" stroke={colors.textPrimary} strokeWidth="1.25" strokeLinecap="round"/>
                        <path d="M17 7L18.4 5.6" stroke={colors.textPrimary} strokeWidth="1.25" strokeLinecap="round"/>
                    </svg>
                </button>

                {/* Timeline button */}
                <button
                    onClick={handleAnalyticsClick}
                    className="ios-button"
                    style={{
                        ...baseButtonStyle,
                        backgroundColor: isRoute('/timeline') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    }}
                    title="Timeline"
                >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="9" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 7V12L15 15" stroke={colors.textPrimary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                {/* Daily Scroll button */}
                <button
                    onClick={() => navigate('/mail')}
                    className="ios-button"
                    style={{
                        ...baseButtonStyle,
                        backgroundColor: isRoute('/mail') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    }}
                    title="Daily Scroll"
                >
                    {/* Scroll icon */}
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={colors.textPrimary} strokeWidth="0.75" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 17V5a2 2 0 0 0-2-2H4"/>
                        <path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3"/>
                    </svg>
                </button>

                {/* Analytics button */}
                <button
                    onClick={() => navigate('/analytics')}
                    className="ios-button"
                    style={{
                        ...baseButtonStyle,
                        backgroundColor: isRoute('/analytics') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    }}
                    title="Analytics"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={colors.textPrimary} strokeWidth="0.75" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 17h3v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-3a3.16 3.16 0 0 0 2-2h1a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1h-1a5 5 0 0 0-2-4V3a4 4 0 0 0-3.2 1.6l-.3.4H11a6 6 0 0 0-6 6v1a5 5 0 0 0 2 4v3a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1z"/>
                        <path d="M16 10h.01"/>
                        <path d="M2 8v1a2 2 0 0 0 2 2h1"/>
                    </svg>
                </button>
            </div>
            
            {/* iOS animation style */}
            <style>
                {`
                    @keyframes ios-tap-pulse {
                        0% { opacity: 1; transform: scale(1); }
                        35% { opacity: 0.4; transform: scale(0.97); }
                        100% { opacity: 1; transform: scale(1); }
                    }
                    
                    .ios-button:active {
                        animation: ios-tap-pulse 180ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
                    }
                    
                    .ios-tapped {
                        animation: ios-tap-pulse 180ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
                    }
                `}
            </style>
            
            {/* User controls */}
            {!user ? (
                <button
                    onClick={onLogin}
                    style={{
                        ...styles.button,
                        ...styles.whiteButton,
                        padding: '6px 16px',
                        fontSize: '14px',
                    }}
                >
                    Sign in
                </button>
            ) : (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    {/* User initial in circle */}
                    <div style={{
                        width: '26px',
                        height: '26px',
                        borderRadius: '50%',
                        backgroundColor: 'transparent',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: colors.textSecondary,
                        fontWeight: '500',
                        fontSize: '12px',
                        border: `1.5px solid ${colors.textSecondary}`,
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                    }} title={user.displayName}>
                        {user.displayName ? user.displayName.charAt(0).toUpperCase() : '?'}
                    </div>
                
                    <button
                        onClick={onLogout}
                        className="ios-button"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            color: colors.textSecondary,
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: '8px',
                            WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                        }}
                        title="Sign out"
                    >
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke={colors.textSecondary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M16 17L21 12L16 7" stroke={colors.textSecondary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M21 12H9" stroke={colors.textSecondary} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
            )}
        </header>
    );
};

export default Header;